import React, { useState, useMemo } from 'react';
import { Heart } from 'lucide-react';
import CalculatorLayout from '../../components/calculators/CalculatorLayout';
import { NumberField } from '../../components/calculators/FormFields';
import { EmptyResult, PrimaryResult, StatRow } from '../../components/calculators/ResultCard';
import { heartRateZones, maxHRTraditional } from '../../lib/calculatorUtils';

const HeartRateZoneCalculatorPage = () => {
  const [age, setAge] = useState('');
  const [resting, setResting] = useState('');

  const result = useMemo(() => {
    const a = parseInt(age, 10);
    if (!a || a < 10 || a > 120) return null;
    const r = parseInt(resting, 10);
    return {
      ...heartRateZones(a, r > 0 ? r : null),
      maxTraditional: maxHRTraditional(a)
    };
  }, [age, resting]);

  const inputs = (
    <div className="space-y-5" data-testid="hr-inputs">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Heart Rate Inputs</h2>
      <p className="text-slate-400 text-sm mb-5">We use the Tanaka formula (more accurate than 220 − age). Adding your resting HR uses Karvonen for personalized zones.</p>
      <NumberField id="hr-age" label="Age" value={age} onChange={setAge} placeholder="e.g. 30" suffix="yrs" min={10} max={120} testId="hr-age-input" required />
      <NumberField id="hr-resting" label="Resting Heart Rate (optional)" value={resting} onChange={setResting} placeholder="e.g. 65" suffix="bpm" min={30} max={120} testId="hr-resting-input" />
    </div>
  );

  const resultNode = result ? (
    <div className="flex flex-col h-full">
      <PrimaryResult label="Max Heart Rate (Tanaka)" value={result.maxHR} unit="bpm" accent="from-rose-500 to-pink-500" testId="hr-result-max" />
      <div className="mt-8 space-y-2">
        {result.zones.map((z, i) => (
          <div key={i} className={`bg-gradient-to-r ${z.color} border border-slate-700/50 rounded-xl p-3 flex items-center justify-between`} data-testid={`hr-zone-${i + 1}`}>
            <div>
              <p className="text-white font-semibold text-sm">{z.name}</p>
              <p className="text-slate-300 text-xs">{z.desc} · {z.pct}</p>
            </div>
            <p className="text-white font-bold text-base sm:text-lg whitespace-nowrap">{z.bpm.min}–{z.bpm.max} <span className="text-xs text-slate-300">bpm</span></p>
          </div>
        ))}
      </div>
      <div className="mt-6 space-y-1">
        <StatRow label="Max HR (220 minus age)" value={result.maxTraditional} unit="bpm" testId="hr-traditional" />
      </div>
      <p className="text-slate-500 text-xs mt-6 leading-relaxed">
        Always consult your physician before high-intensity training, especially if you have cardiovascular concerns.
      </p>
    </div>
  ) : (
    <EmptyResult message="Enter your age to see your training heart rate zones." />
  );

  return (
    <CalculatorLayout
      slug="heart-rate-zones" title="Heart Rate Zone Calculator"
      subtitle="Calculate your max heart rate and training zones for fat burn, aerobic, threshold and peak."
      metaDescription="Heart Rate Zone Calculator with Tanaka and Karvonen-based training zones for cardio, HIIT and fat burn."
      icon={Heart}
      accentGradient="from-rose-500 to-pink-500" accentColor="text-rose-400"
      badgeBg="bg-rose-500/10" badgeBorder="border-rose-500/30"
      inputs={inputs} result={resultNode}
      formulaText={<>
        <p><strong className="text-slate-300">Max HR (Tanaka):</strong> 208 − 0.7 × age (more accurate than 220 − age)</p>
        <p><strong className="text-slate-300">Karvonen (with resting HR):</strong> Target HR = RestingHR + intensity% × (MaxHR − RestingHR)</p>
      </>}
    />
  );
};

export default HeartRateZoneCalculatorPage;
